<template>
  <vs-popup
    class="tasting-group-create-popup"
    id="popup"
    :title="updateMode ? 'Update Taste Panel' : 'Create Taste Panel'"
    :active.sync="open"
    :buttons-hidden="true"
    @close="open = false"
  >
    <div class="flex justify-end">
      <vs-button
        type="border"
        size="normal"
        color="warning"
        v-can="'create-edit-quality-control-tastings'"
        v-if="isResultsAvailable"
        class="mb-2"
        @click="onClickShowResults"
      >
        Show Results
      </vs-button>
    </div>

    <vx-card title="Assigned Staff" class="mb-6 shadow-md">
      <v-select
        name="timescale_type"
        v-model="selectedStaffList"
        :options="staffs"
        :closeOnSelect="false"
        :clearable="false"
        :dir="$vs.rtl ? 'rtl' : 'ltr'"
        label="name"
        multiple
      />
    </vx-card>

    <div v-if="step === 1" class="flex flex-col flex-1">
      <vx-card title="Work Orders" class="mb-6 shadow-sm">
        <div slot="no-body" class="p-4" v-can="'view-work-orders'">
          <div class="vx-col w-full md:w-1/4 sm:w-1/2 mb-3">
            <label class="block mb-2 text-sm font-semibold">Search</label>
            <vs-input
              class="w-full"
              type="search"
              placeholder="Search work orders..."
              v-model="woSearchTxt"
              @input="searchWo(woSearchTxt)"
            />
          </div>

          <vs-table
            id="wo-list-table"
            :data="woList"
            :sst="true"
            v-model="selectedWoList"
            :multiple="true"
            :selectable="true"
          >
            <template slot="thead">
              <vs-th>WORK ORDER CODE</vs-th>
              <vs-th>ITS PRODUCT CODE/NAME</vs-th>
              <vs-th>USED IN OTHER TASTE PANELS</vs-th>
            </template>

            <template slot-scope="{ data }">
              <vs-tr
                v-for="(tr, indextr) in data"
                :key="indextr"
                :data="tr"
                style="cursor: pointer"
                :class="{ 'disabled-row': canSelectRow(tr) }"
              >
                <vs-td>{{ tr.work_order_code }}</vs-td>
                <vs-td><HyperLink :id="tr.formulation.id" type="formulation">{{ tr.formulation.formulation_code }}</HyperLink></vs-td>
                <vs-td
                  ><span v-if="tr.groups && tr.groups.length > 0">
                    <HyperLink
                      :id="getLatestGroup(tr.groups).id"
                      type="tasting-group"
                      class="enabled-link"
                      >{{
                        `${
                          getLatestGroup(tr.groups).code
                        } (${getSignoffStatusForWorkOrder(tr)})`
                      }}</HyperLink
                    > </span
                  ><span v-else>-</span></vs-td
                >
              </vs-tr>
            </template>
          </vs-table>
        </div>
      </vx-card>

      <vx-card title="Raw Material Inventory" class="mb-6 shadow-sm">
        <div slot="no-body" class="p-4" v-can="'view-work-orders'">
          <div class="vx-col w-full md:w-1/4 sm:w-1/2 mb-3">
            <label class="block mb-2 text-sm font-semibold">Search</label>
            <vs-input
              class="w-full"
              type="search"
              placeholder="Search raw material..."
              v-model="rmInventorySearchTxt"
              @input="searchRmInventory(rmInventorySearchTxt)"
            />
          </div>

          <vs-table
            id="rm-list-table"
            :data="rmInventoryList"
            :sst="true"
            v-model="selectedRmInventoryList"
            :multiple="true"
            :selectable="true"
          >
            <template slot="thead">
              <vs-th>BATCH NO</vs-th>
              <vs-th>RAW MATERIAL</vs-th>
              <vs-th>SUPPLIER</vs-th>
              <vs-th>USED IN OTHER TASTING PANELS</vs-th>
              <vs-th>QC TASTE INSTRUCTIONS</vs-th>
            </template>

            <template slot-scope="{ data }">
              <vs-tr
                v-for="(tr, indextr) in data"
                :key="indextr"
                :data="tr"
                style="cursor: pointer"
                :class="{ 'disabled-row': disableRmBatch(tr) }"
              >
                <vs-td>{{ tr.batch_no }}</vs-td>
                <vs-td
                  ><HyperLink
                    :id="tr.raw_material.id"
                    :supplier_id="tr.raw_material.supplier.id"
                    type="raw-material"
                    >{{
                      tr.raw_material.company_product_code +
                        " - " +
                        tr.raw_material.company_product_name || ""
                    }}</HyperLink
                  ></vs-td
                >
                <vs-td
                  ><HyperLink
                    :id="tr.raw_material.supplier.id"
                    type="supplier"
                    >{{
                      tr.raw_material.supplier.company_name || ""
                    }}</HyperLink
                  ></vs-td
                >
                <vs-td
                  ><span v-if="tr.groups.length > 0"
                    ><HyperLink :id="tr.groups[0].id" type="tasting-group">{{
                      tr.groups[0].code
                    }}</HyperLink></span
                  ><span v-else>-</span></vs-td
                >
                <vs-td>
                  <vs-chip color="danger" v-if="isDoNotSample(tr)">
                    DNS
                  </vs-chip>
                </vs-td>
              </vs-tr>
            </template>
          </vs-table>
          <!-- <vs-pagination v-on:changeMaxItems="changePage" :size-array="meta.total ? meta.total : 20"
                :max-items="perPage" description-body="Rows per page" description-connector="of"
                description-title="Records" description :description-items="descriptionItems"
                :total="(meta.last_page) ? meta.last_page : 0" v-model="page" class="p-5">
          </vs-pagination> -->
        </div>
      </vx-card>

      <div class="flex justify-end mb-6">
        <vs-button
          icon-pack="feather"
          icon="icon-arrow-right"
          color="primary"
          v-can="'create-edit-quality-control-tastings'"
          @click="nextStep"
        >
          Next
        </vs-button>
      </div>
    </div>

    <div v-if="step === 2" class="flex flex-col flex-1">
      <vx-card title="Selected Work Orders" class="mb-6 shadow-md">
        <DraggableTable
          :rows="selectedWoList"
          :columns="[{ label: 'Work Order Code', key: 'work_order_code' }, {label:'ITS Product Code/Name', key:'formulation.formulation_code'}]"
          :showCheckbox="false"
          type="work-order"
          idKey="id"
          @reordered="updateWorkOrderOrder"
          @onClickShowLastTestedBatches="onClickShowLastTestedBatches"
        />
      </vx-card>

      <vx-card title="Selected Raw Materials" class="mb-6 shadow-md">
        <DraggableTable
          :rows="selectedRmInventoryList"
          :columns="[{ label: 'Batch No', key: 'batch_no' }]"
          :showCheckbox="false"
          type="raw-material"
          idKey="id"
          @reordered="updateRawMaterialOrder"
          @onClickShowLastTestedBatches="onClickShowLastTestedBatches"
        />
      </vx-card>

      <div class="flex items-center justify-between mb-6">
        <vs-button
          icon-pack="feather"
          icon="icon-arrow-left"
          color="warning"
          v-can="'create-edit-quality-control-tastings'"
          @click="prevStep"
        >
          Back
        </vs-button>

        <vs-button
          icon-pack="feather"
          icon="icon-save"
          color="primary"
          v-can="'create-edit-quality-control-tastings'"
          @click="submitForm"
        >
          Submit
        </vs-button>
      </div>
    </div>
    <ShowTastePanelResultPopUp ref="showTastePanelResultPopUp" />
    <show-last-tested-batch-result-pop-up
      ref="showLastTestedBatchResultPopUp"
      @onSelectBatch="onSelectBatch"
    />
  </vs-popup>
</template>
<script>
import { RepositoryFactory } from "@/repository/RepositoryFactory";
import SuccessMessage from "@/mixins/SuccessMessage";
import ErrorMessages from "@/mixins/ErrorMessages";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import draggable from "vuedraggable";
import DraggableTable from "../../table/DraggableTable.vue";
import { mapActions, mapGetters } from "vuex";
import ShowTastePanelResultPopUp from "./ShowTastePanelResultPopUp.vue";
import ShowLastTestedBatchResultPopUp from "@/components/popups/TastePanel/ShowLastTestedBatchResultPopUp.vue";

const TriangleTestsRepository = RepositoryFactory.get("triangle_tests");
const ManufacturingRepository = RepositoryFactory.get("manufacturing");

function initialState() {
  return {
    woList: [],
    selectedWoList: [],
    rmInventoryList: [],
    selectedRmInventoryList: [],
    updateMode: false,
    open: false,
    woSearchTxt: "",
    rmInventorySearchTxt: "",
    statusList: [],
    status: "",
    woListOrder: [],
    step: 1,
    selectedStaffList: [],
    signOffDetails: {},
    isCreateByBatch: false,
    groupDetails: {},
  };
}
export default {
  name: "TastingGroupCreate",
  data() {
    return {
      ...initialState(),
    };
  },
  methods: {
    ...mapActions(["fetchStaffs"]),
    async openPopup(id = null, code = null, type = null) {
      try {
        await Object.assign(this.$data, initialState());
        if (type == "raw_material") {
          this.rmInventorySearchTxt = code;
          this.searchRmInventory(this.rmInventorySearchTxt);
          // this.isCreateByBatch = true;
        } else if (type == "work_order") {
          this.woSearchTxt = code;
          this.getListOfWo(this.woSearchTxt);
        } else if (id != null) {
          this.id = id;
          this.updateMode = true;
          await this.fetchTastingGroupStatus();
          await this.fetchTestingGroupDetails(id);
        }
        await this.fetchStaffs();
        this.open = true;
      } catch (error) {}
    },
    async getListOfWo(searchText = "") {
      try {
        if (searchText == "") {
          this.woList = [...this.selectedWoList];
          return;
        }
        this.$vs.loading();
        const response = await ManufacturingRepository.getWoForTastePanel(
          `filter[search]=${searchText}`
        );
        const filtered = response.data.data.filter(
          (item) => !this.selectedWoList.some((sel) => sel.id === item.id)
        ).map((item) => ({
          ...item,
          last_tested_batch: null,
        }));


        this.woList = [...this.selectedWoList, ...filtered];
      } catch (e) {
      } finally {
        this.$vs.loading.close();
      }
    },
    canSelectRow(row) {
      const status = this.getSignoffStatusForWorkOrder(row);
      return ["Passed", "Failed", "Pending"].includes(status);
    },
    isDoNotSample(row) {
      return (
        row.raw_material.raw_material_company_data &&
        row.raw_material.raw_material_company_data.test_type === "Do_Not_Sample"
      );
    },
    disableRmBatch(row) {
      if (!this.isDoNotSample(row)) {
        if (
          row.groups.length === 0 ||
          (row.latest_quality_control_batch_sign_off &&
            row.latest_quality_control_batch_sign_off.status ==
              "Failed & Retest")
        ) {
          return false;
        }
        return true;
      } else {
        return true;
      }
    },
    onClickShowLastTestedBatches(row, type) {
     
      this.$refs.showLastTestedBatchResultPopUp.openPopup(row, type);
    },
    onSelectBatch(selectedLastBatchId, type, item){

      if(type == "raw-material"){
        const inventoryIndex = this.selectedRmInventoryList.findIndex((r)=> r.id == item.id)
        this.selectedRmInventoryList[inventoryIndex].last_tested_batch = selectedLastBatchId
  
      }
      if(type == "work-order"){
        const workOrderIndex = this.selectedWoList.findIndex((r)=> r.id == item.id)
        this.selectedWoList[workOrderIndex].last_tested_batch = selectedLastBatchId
        
      }

    },
    getLatestGroup(groups) {
      if (!groups || !groups.length) {
        return null;
      }

      return groups[groups.length - 1];
    },
    getSignoffStatusForWorkOrder(workOrder) {
      const groups = workOrder.groups || [];

      if (groups.length === 0) {
        return "";
      }

      const latestGroup = this.getLatestGroup(groups);
      const signOffs = latestGroup.sign_offs || [];

      if (signOffs.length === 0) {
        return "Pending";
      }

      return signOffs[signOffs.length - 1].status;
    },
    async getInventoryList(searchText = "") {
      if (!searchText) {
        this.rmInventoryList = [...this.selectedRmInventoryList];
        return;
      }

      this.$vs.loading();

      try {
        const { data } =
          await ManufacturingRepository.getRawMaterialStockForTastePanel(
            `filter[search]=${searchText}`
          );

        const existingIds = new Set(
          this.selectedRmInventoryList.map((item) => item.id)
        );
        const newItems = data.data
          .filter((item) => !existingIds.has(item.id))
          .map((item) => ({
            id: item.id,
            groups: item.groups,
            last_tested_batch: null,
            batch_no: item.code,
            latest_quality_control_tasting: item.latest_quality_control_tasting,
            latest_quality_control_batch_sign_off: item.latest_quality_control_batch_sign_off,
            raw_material: item.raw_material,
          }));

        this.rmInventoryList = [...this.selectedRmInventoryList, ...newItems];

        if (this.isCreateByBatch) {
          this.selectedRmInventoryList = [...newItems];
        }
      } catch (error) {
      } finally {
        this.$vs.loading.close();
      }
    },
    async fetchTestingGroupDetails(id) {
      try {
        this.$vs.loading();
        const response = await TriangleTestsRepository.getTastingGroup(id);
        this.groupDetails = response.data.data || {};
        this.selectedWoList = response.data.data.work_orders
          .map((x) => {
            return {
              ...x,
              formulation_code: x.formulation.formulation_code,
              order: x.pivot.order,
              last_tested_batch: x.pivot.last_tested_batch ? x.pivot.last_tested_batch : null
            };
          })
          .sort((a, b) => a.order - b.order);

        this.selectedRmInventoryList =
          response.data.data.raw_material_stock_inventories
            .map((x) => {
              return {
                id: x.id,
                batch_no: x.code,
                latest_quality_control_batch_sign_off:
                  x.latest_quality_control_batch_sign_off,
                groups: x.groups ? x.groups : [],
                last_tested_batch: x.pivot.last_tested_batch ? x.pivot.last_tested_batch : null,
                latest_quality_control_tasting: x.latest_quality_control_tasting
                  ? x.latest_quality_control_tasting.group_id
                  : null,
                raw_material: x.staff_raw_material_order_item.raw_material,
                order: x.pivot.order,
              };
            })
            .sort((a, b) => a.order - b.order);
        this.selectedStaffList = response.data.data.staff.map((x) => {
          return {
            value: x.id,
            name: x.name,
          };
        });
        if (this.updateMode) {
          this.woList = [...this.selectedWoList];
          this.rmInventoryList = [...this.selectedRmInventoryList];
        }

        this.status = response.data.data.status;
      } catch (e) {
      } finally {
        this.$vs.loading.close();
      }
    },
    async fetchTastingGroupStatus() {
      try {
        this.$vs.loading();
        const response =
          await TriangleTestsRepository.getListOfTastingGroupSignOffStatuses();
        this.statusList = response.data.data;
      } catch (e) {
      } finally {
        this.$vs.loading.close();
      }
    },

    async getTastingGroupSignOff() {
      try {
        this.$vs.loading();
        const response = await TriangleTestsRepository.getTastingGroupSignOff(
          this.id
        );

        this.status = response.data.data.status;
        this.signOffDetails = response.data.data;
      } catch (e) {
      } finally {
        this.$vs.loading.close();
      }
    },

    async onClickShowResults() {
      this.$refs.showTastePanelResultPopUp.openPopUp(this.id);
    },

    async cloneTastingGroup() {
      try {
        this.$vs.loading();
        const response = await TriangleTestsRepository.cloneTastingGroup(
          this.id
        );
        this.$emit("refresh");
      } catch (e) {
      } finally {
        this.$vs.loading.close();
      }
    },

    async createOrUpdateTastingGroupStatus(status) {
      try {
        this.$vs.loading();
        const response =
          await TriangleTestsRepository.createTastingGroupSignOff(this.id, {
            status: status,
            comment: "test",
          });
        this.$emit("refresh");
      } catch (e) {
      } finally {
        this.$vs.loading.close();
      }
    },
    searchWo: _.debounce(async function (search) {
      await this.getListOfWo(search);
    }, 1000),
    searchRmInventory: _.debounce(async function (search) {
      await this.getInventoryList(search);
    }, 1000),

    isSelected(id) {
      return this.selectedWoList.includes(id);
    },

    onReorder() {
      //
    },

    nextStep() {
      this.step = this.step + 1;
    },

    prevStep() {
      this.step = this.step - 1;
    },
    updateWorkOrderOrder(list) {
      this.selectedWoList = list;
    },
    updateRawMaterialOrder(list) {
      this.selectedRmInventoryList = list;
    },
    async submitForm() {
      try {
        this.$vs.loading();
        const payload = {
          work_orders: this.selectedWoList.map((item, index) => {
            return { id: item.id, order: index + 1 , last_tested_batch: item.last_tested_batch};
          }),
          raw_material_stock_inventories: this.selectedRmInventoryList.map(
            (item, index) => {
              return { id: item.id, order: index + 1, last_tested_batch: item.last_tested_batch };
            }
          ),
          staff: this.selectedStaffList.map((item) => item.value),
        };
        if (this.updateMode) {
          const response = await TriangleTestsRepository.updateTastingGroup(
            this.id,
            payload
          );
        } else {
          const response = await TriangleTestsRepository.createTastingGroup(
            payload
          );
        }
        this.$emit("refresh");
        this.open = false;
      } catch (e) {
        this.convertAndNotifyError(e);
      } finally {
        this.$vs.loading.close();
      }
    },
  },
  components: {
    "v-select": vSelect,
    draggable,
    DraggableTable,
    ShowTastePanelResultPopUp,
    ShowLastTestedBatchResultPopUp,
  },
  mixins: [SuccessMessage, ErrorMessages],
  computed: {
    ...mapGetters(["staffs"]),
    isResultsAvailable() {
      if (!this.updateMode) return false;

      const details = this.groupDetails;

      if (!details || Object.keys(details).length === 0) return false;

      const hasRawMaterials =
        Array.isArray(details.raw_material_stock_inventories) &&
        details.raw_material_stock_inventories.length > 0;

      const hasWorkOrders =
        Array.isArray(details.work_orders) && details.work_orders.length > 0;

      return hasRawMaterials || hasWorkOrders;
    },
  },
};
</script>
<style scoped>
.tasting-group-create-popup >>> .vs-popup {
  min-width: 80% !important;
}

.disabled-row {
  opacity: 0.5;
  pointer-events: none;
}

.enabled-link {
  pointer-events: auto;
}
</style>
